/**
 * Product Ingredients & Allergens Component
 *
 * Handles responsive tab/accordion functionality with accessibility support
 * Implements WAI-ARIA Tabs Pattern (desktop) and Disclosure Pattern (mobile)
 */

class ProductIngredientsAllergens {
  constructor(sectionId) {
    this.sectionId = sectionId;
    this.section = document.querySelector(`[data-section-id="${sectionId}"]`);

    if (!this.section) {
      console.warn(`ProductIngredientsAllergens: Section with ID "${sectionId}" not found`);
      return;
    }

    // Prevent multiple instances on the same section
    if (this.section.hasAttribute('data-product-ingredients-allergens-initialized')) {
      console.warn(`ProductIngredientsAllergens: Section "${sectionId}" already initialized`);
      return;
    }

    // Mark section as initialized
    this.section.setAttribute('data-product-ingredients-allergens-initialized', 'true');

    // Configuration
    this.config = {
      transitionDuration: 250,
      mobileBreakpoint: 1024,
      deepLinkMap: {
        'description': 'description',
        'ingredients': 'ingredients',
        'nutrition': 'nutrition',
        'nutritional-info': 'nutrition',
        'faq': 'faq',
        'faqs': 'faq'
      }
    };

    // State
    this.state = {
      activePanel: this.section.dataset.defaultPanel || null,
      mobileBehavior: this.section.dataset.mobileBehavior || 'accordion',
      isDesktop: window.innerWidth >= this.config.mobileBreakpoint,
      isInitialized: false
    };

    // DOM elements
    this.elements = {
      tablist: this.section.querySelector('.product-ingredients-allergens__tablist'),
      tabs: this.section.querySelectorAll('.product-ingredients-allergens__tab'),
      panels: this.section.querySelectorAll('.product-ingredients-allergens__panel'),
      accordionHeaders: this.section.querySelectorAll('.product-ingredients-allergens__accordion-header'),
      faqItems: this.section.querySelectorAll('[data-faq-item]')
    };

    // Bound event handlers for proper cleanup
    this._handleTabClick = this.handleTabClick.bind(this);
    this._handleTabKeydown = this.handleTabKeydown.bind(this);
    this._handleAccordionClick = this.handleAccordionClick.bind(this);
    this._handleAccordionKeydown = this.handleAccordionKeydown.bind(this);
    this._handleResize = this.debounce(this.handleResize.bind(this), 100);
    this._handleHashChange = this.handleHashChange.bind(this);
    this._handleGlobalKeydown = this.handleGlobalKeydown.bind(this);
    this._handleFAQClick = this.handleFAQClick.bind(this);
    this._handleFAQKeydown = this.handleFAQKeydown.bind(this);

    this.init();
  }

  /**
   * Initialize the component
   */
  init() {
    try {
      this.setupEventListeners();
      this.initializeResponsiveBehavior();
      this.handleDeepLink();
      this.setupFAQAccordions();
      this.announceInitialState();
      this.state.isInitialized = true;

      // Cache content for performance
      this.cacheContent();

      console.log('ProductIngredientsAllergens: Initialized successfully');
    } catch (error) {
      console.error('ProductIngredientsAllergens: Initialization failed', error);
    }
  }


  /**
   * Initialize responsive behavior based on viewport
   */
  initializeResponsiveBehavior() {
    this.state.isDesktop = window.innerWidth >= this.config.mobileBreakpoint;

    if (this.state.isDesktop) {
      // Desktop: Show the active panel from section settings
      this.showDesktopActivePanel();
    } else {
      // Mobile: Collapse all panels (accordion starts closed)
      this.collapseAllPanels();
    }
  }

  /**
   * Show the desktop active panel (one tab always active on desktop)
   */
  showDesktopActivePanel() {
    const activePanelId = this.state.activePanel;
    if (activePanelId) {
      const activePanel = this.section.querySelector(`#panel-${activePanelId}`);
      if (activePanel) {
        activePanel.hidden = false;
        activePanel.classList.add('product-ingredients-allergens__panel--active');
      }

      // Set active state on the tab
      const activeTab = this.section.querySelector(`.product-ingredients-allergens__tab[data-panel="${activePanelId}"]`);
      if (activeTab) {
        activeTab.setAttribute('aria-selected', 'true');
        activeTab.classList.add('product-ingredients-allergens__tab--active');
      }
    }
  }

  /**
   * Setup all event listeners
   */
  setupEventListeners() {
    // Tab clicks (desktop)
    this.elements.tabs.forEach(tab => {
      tab.addEventListener('click', this._handleTabClick);
      tab.addEventListener('keydown', this._handleTabKeydown);
    });

    // Accordion header clicks (mobile)
    this.elements.accordionHeaders.forEach(header => {
      header.addEventListener('click', this._handleAccordionClick);
      header.addEventListener('keydown', this._handleAccordionKeydown);
    });

    // Window resize
    window.addEventListener('resize', this._handleResize);

    // Hash change for deep links
    window.addEventListener('hashchange', this._handleHashChange);

    // Escape key for closing accordions
    document.addEventListener('keydown', this._handleGlobalKeydown);
  }

  /**
   * Handle tab click (desktop)
   */
  handleTabClick(event) {
    event.preventDefault();
    const tab = event.currentTarget;
    const panelId = tab.dataset.panel;

    if (panelId && panelId !== this.state.activePanel) {
      this.activatePanel(panelId, 'tab');
      this.updateURL(panelId);
    }

    // Ensure the clicked tab receives focus
    tab.focus();
  }

  /**
   * Handle tab keyboard navigation (desktop)
   */
  handleTabKeydown(event) {
    const { key } = event;
    const currentTab = event.currentTarget;
    const tabs = Array.from(this.elements.tabs);
    const currentIndex = tabs.indexOf(currentTab);

    switch (key) {
      case 'ArrowLeft':
      case 'ArrowUp':
        event.preventDefault();
        this.focusTab(currentIndex - 1, tabs);
        break;

      case 'ArrowRight':
      case 'ArrowDown':
        event.preventDefault();
        this.focusTab(currentIndex + 1, tabs);
        break;

      case 'Home':
        event.preventDefault();
        this.focusTab(0, tabs);
        break;

      case 'End':
        event.preventDefault();
        this.focusTab(tabs.length - 1, tabs);
        break;

      case 'Enter':
      case ' ':
        event.preventDefault();
        this.handleTabClick(event);
        break;
    }
  }

  /**
   * Handle accordion header click (mobile)
   */
  handleAccordionClick(event) {
    event.preventDefault();
    const header = event.currentTarget;
    const panelId = header.dataset.panel;
    const isExpanded = header.getAttribute('aria-expanded') === 'true';

    if (this.state.mobileBehavior === 'accordion') {
      // Accordion behavior: toggle current, close others
      if (isExpanded) {
        this.collapsePanel(panelId);
      } else {
        this.activatePanel(panelId, 'accordion');
      }
    } else {
      // Vertical tabs behavior: activate panel
      if (panelId !== this.state.activePanel) {
        this.activatePanel(panelId, 'vertical_tabs');
      }
    }

    this.updateURL(panelId);
  }

  /**
   * Handle accordion keyboard navigation (mobile)
   */
  handleAccordionKeydown(event) {
    const { key } = event;

    switch (key) {
      case 'Enter':
      case ' ':
        event.preventDefault();
        this.handleAccordionClick(event);
        break;

      case 'Escape':
        event.preventDefault();
        this.collapsePanel(event.currentTarget.dataset.panel);
        break;
    }
  }

  /**
   * Handle global keyboard events
   */
  handleGlobalKeydown(event) {
    if (event.key === 'Escape') {
      // Close any open accordion panels on mobile
      if (!this.state.isDesktop && this.state.mobileBehavior === 'accordion') {
        this.collapseAllPanels();
      }
    }
  }

  /**
   * Focus a specific tab by index
   */
  focusTab(index, tabs) {
    const targetIndex = ((index % tabs.length) + tabs.length) % tabs.length;
    const targetTab = tabs[targetIndex];

    if (targetTab) {
      targetTab.focus();

      // Activate the tab when focused via keyboard
      const panelId = targetTab.dataset.panel;
      if (panelId && panelId !== this.state.activePanel) {
        this.activatePanel(panelId, 'keyboard');
        this.updateURL(panelId);
      }
    }
  }

  /**
   * Activate a panel
   */
  activatePanel(panelId, source = 'programmatic') {
    if (!panelId) return;

    try {
      const targetPanel = this.section.querySelector(`#panel-${panelId}`);
      if (!targetPanel) {
        console.warn(`ProductIngredientsAllergens: Panel "${panelId}" not found`);
        return;
      }

      // Close other panels if in accordion mode
      if (!this.state.isDesktop && this.state.mobileBehavior === 'accordion') {
        this.collapseAllPanels();
      } else {
        // Desktop or vertical tabs: hide all panels
        this.elements.panels.forEach(panel => {
          panel.hidden = true;
          panel.classList.remove('product-ingredients-allergens__panel--active');
        });
      }

      // Update tabs (desktop)
      this.elements.tabs.forEach(tab => {
        const isActive = tab.dataset.panel === panelId;
        tab.setAttribute('aria-selected', isActive);
        tab.classList.toggle('product-ingredients-allergens__tab--active', isActive);
      });

      // Update accordion headers (mobile)
      this.elements.accordionHeaders.forEach(header => {
        const isActive = header.dataset.panel === panelId;
        header.setAttribute('aria-expanded', isActive);
        header.classList.toggle('product-ingredients-allergens__accordion-header--active', isActive);
      });

      // Show target panel
      targetPanel.hidden = false;
      targetPanel.classList.add('product-ingredients-allergens__panel--active');

      // Update state
      this.state.activePanel = panelId;

      // Refresh FAQ accordions if FAQ panel is activated
      if (panelId === 'faq') {
        setTimeout(() => {
          this.refreshFAQAccordions();
        }, 50);
      }

      // Announce change to screen readers
      this.announceChange(panelId, source);

      // Trigger custom event
      this.section.dispatchEvent(new CustomEvent('product-ingredients-allergens:change', {
        detail: { panelId, source }
      }));

    } catch (error) {
      console.error('ProductIngredientsAllergens: Error activating panel', error);
    }
  }

  /**
   * Collapse a specific panel
   */
  collapsePanel(panelId) {
    const targetPanel = this.section.querySelector(`#panel-${panelId}`);

    // Update ALL elements with this panel ID (both desktop tab and mobile accordion header)
    const allHeaders = this.section.querySelectorAll(`[data-panel="${panelId}"]`);

    if (targetPanel) {
      targetPanel.hidden = true;
      targetPanel.classList.remove('product-ingredients-allergens__panel--active');
    }

    // Update all headers (desktop tab + mobile accordion header)
    allHeaders.forEach(header => {
      header.setAttribute('aria-expanded', 'false');
      header.setAttribute('aria-selected', 'false');
      header.classList.remove('product-ingredients-allergens__accordion-header--active');
      header.classList.remove('product-ingredients-allergens__tab--active');
    });
  }

  /**
   * Collapse all panels
   */
  collapseAllPanels() {
    this.elements.panels.forEach(panel => {
      panel.hidden = true;
      panel.classList.remove('product-ingredients-allergens__panel--active');
    });

    this.elements.accordionHeaders.forEach(header => {
      header.setAttribute('aria-expanded', 'false');
      header.classList.remove('product-ingredients-allergens__accordion-header--active');
    });
  }

  /**
   * Handle deep links from URL hash
   */
  handleDeepLink() {
    const hash = window.location.hash.slice(1).toLowerCase();

    if (!hash) {
      return;
    }

    const panelId = this.config.deepLinkMap[hash];
    if (panelId && this.isPanelAvailable(panelId)) {
      this.activatePanel(panelId, 'deeplink');
    } else {
      // Invalid hash, redirect to first available panel
      this.redirectToFirstAvailable();
    }
  }

  /**
   * Handle hash change events
   */
  handleHashChange() {
    this.handleDeepLink();
  }

  /**
   * Check if a panel is available
   */
  isPanelAvailable(panelId) {
    const panel = this.section.querySelector(`#panel-${panelId}`);
    return panel !== null;
  }

  /**
   * Redirect to first available panel
   */
  redirectToFirstAvailable() {
    // Find the first available panel
    const firstPanel = this.elements.panels[0];

    if (firstPanel) {
      const panelId = firstPanel.id.replace('panel-', '');

      if (panelId && this.isPanelAvailable(panelId)) {
        this.activatePanel(panelId, 'fallback');
        this.updateURL(panelId);

        return;
      }
    }

    // Fallback to already active panel if exists
    if (this.state.activePanel) {
      this.updateURL(this.state.activePanel);
    }
  }

  /**
   * Update URL hash
   */
  updateURL(panelId) {
    const hashMap = {
      'description': 'description',
      'ingredients': 'ingredients',
      'nutrition': 'nutrition',
      'faq': 'faq'
    };

    const hash = hashMap[panelId];
    if (hash) {
      const newURL = `${window.location.pathname}${window.location.search}#${hash}`;
      history.replaceState(null, '', newURL);
    }
  }

  /**
   * Setup FAQ accordion functionality using event delegation
   */
  setupFAQAccordions() {
    // Use event delegation on the section element to handle FAQ clicks
    this.section.addEventListener('click', this._handleFAQClick);

    // Handle keyboard events for FAQ toggles
    this.section.addEventListener('keydown', this._handleFAQKeydown);
  }

  /**
   * Handle FAQ click events (delegated)
   */
  handleFAQClick(event) {
    const toggle = event.target.closest('[data-faq-toggle]');
    if (!toggle) return;

    event.preventDefault();
    this.toggleFAQItem(toggle);
  }

  /**
   * Handle FAQ keyboard events (delegated)
   */
  handleFAQKeydown(event) {
    const toggle = event.target.closest('[data-faq-toggle]');
    if (!toggle) return;

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.toggleFAQItem(toggle);
    } else if (event.key === 'Escape') {
      event.preventDefault();
      const content = toggle.closest('.faq-item').querySelector('.faq-item__content');
      if (content) {
        toggle.setAttribute('aria-expanded', 'false');
        content.hidden = true;
      }
    }
  }

  /**
   * Toggle a specific FAQ item
   */
  toggleFAQItem(toggle) {
    const faqItem = toggle.closest('.faq-item');
    const content = faqItem.querySelector('.faq-item__content');

    if (!content) return;

    const isExpanded = toggle.getAttribute('aria-expanded') === 'true';

    // Toggle current FAQ
    toggle.setAttribute('aria-expanded', !isExpanded);
    content.hidden = isExpanded;

    // Close other FAQs (single-open behavior)
    const allFaqItems = this.section.querySelectorAll('.faq-item');
    allFaqItems.forEach(otherItem => {
      if (otherItem !== faqItem) {
        const otherToggle = otherItem.querySelector('[data-faq-toggle]');
        const otherContent = otherItem.querySelector('.faq-item__content');
        if (otherToggle && otherContent) {
          otherToggle.setAttribute('aria-expanded', 'false');
          otherContent.hidden = true;
        }
      }
    });
  }

  /**
   * Refresh FAQ accordions - useful when content is dynamically added
   */
  refreshFAQAccordions() {
    // Find all FAQ items and ensure they have proper initial state
    const faqItems = this.section.querySelectorAll('.faq-item');
    faqItems.forEach((faqItem, index) => {
      const toggle = faqItem.querySelector('[data-faq-toggle]');
      const content = faqItem.querySelector('.faq-item__content');

      if (toggle && content) {
        // Check current aria-expanded state to respect server-side rendering
        const isExpanded = toggle.getAttribute('aria-expanded') === 'true';

        // Sync the hidden attribute with aria-expanded state
        content.hidden = !isExpanded;

        // If no aria-expanded is set, default to collapsed (except first item)
        if (!toggle.hasAttribute('aria-expanded')) {
          const shouldExpand = index === 0; // First item expanded by default
          toggle.setAttribute('aria-expanded', shouldExpand.toString());
          content.hidden = !shouldExpand;
        }
      }
    });

    console.log('FAQ accordions refreshed:', faqItems.length, 'items found');
  }

  /**
   * Handle window resize
   */
  handleResize() {
    const wasDesktop = this.state.isDesktop;
    this.state.isDesktop = window.innerWidth >= this.config.mobileBreakpoint;

    if (wasDesktop !== this.state.isDesktop) {
      // Layout changed from desktop to mobile or vice versa
      if (this.state.isDesktop) {
        // Switched to desktop: Show active panel
        this.showDesktopActivePanel();
      } else {
        // Switched to mobile: Collapse all panels
        this.collapseAllPanels();
      }
    }
  }


  /**
   * Announce changes to screen readers
   */
  announceChange(panelId, source) {
    const panelLabels = {
      'description': 'Description',
      'ingredients': 'Ingredients',
      'nutrition': 'Nutritional Information',
      'faq': 'Frequently Asked Questions'
    };

    const label = panelLabels[panelId] || panelId;
    const message = `${label} panel selected`;

    this.announceToScreenReader(message);
  }

  /**
   * Announce initial state
   */
  announceInitialState() {
    if (this.state.activePanel) {
      this.announceChange(this.state.activePanel, 'initial');
    }
  }

  /**
   * Announce message to screen readers
   */
  announceToScreenReader(message) {
    const announcement = document.createElement('div');
    announcement.setAttribute('aria-live', 'polite');
    announcement.setAttribute('aria-atomic', 'true');
    announcement.className = 'sr-only';
    announcement.textContent = message;

    document.body.appendChild(announcement);

    setTimeout(() => {
      document.body.removeChild(announcement);
    }, 1000);
  }

  /**
   * Cache content for performance
   */
  cacheContent() {
    this.contentCache = new Map();

    this.elements.panels.forEach(panel => {
      const panelId = panel.id.replace('panel-', '');
      const content = panel.querySelector('.product-ingredients-allergens__content');
      if (content) {
        this.contentCache.set(panelId, content.innerHTML);
      }
    });
  }

  /**
   * Debounce utility function
   */
  debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func.apply(this, args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  /**
   * Public API: Activate a specific panel
   */
  activate(panelId) {
    if (this.isPanelAvailable(panelId)) {
      this.activatePanel(panelId, 'api');
      this.updateURL(panelId);
    }
  }

  /**
   * Public API: Get current active panel
   */
  getActivePanel() {
    return this.state.activePanel;
  }

  /**
   * Public API: Check if component is initialized
   */
  isInitialized() {
    return this.state.isInitialized;
  }

  /**
   * Public API: Destroy the component
   */
  destroy() {
    // Remove event listeners
    this.elements.tabs.forEach(tab => {
      tab.removeEventListener('click', this._handleTabClick);
      tab.removeEventListener('keydown', this._handleTabKeydown);
    });

    this.elements.accordionHeaders.forEach(header => {
      header.removeEventListener('click', this._handleAccordionClick);
      header.removeEventListener('keydown', this._handleAccordionKeydown);
    });

    // Remove window and document listeners
    window.removeEventListener('resize', this._handleResize);
    window.removeEventListener('hashchange', this._handleHashChange);
    document.removeEventListener('keydown', this._handleGlobalKeydown);

    // Remove FAQ delegated listeners
    this.section.removeEventListener('click', this._handleFAQClick);
    this.section.removeEventListener('keydown', this._handleFAQKeydown);

    // Clear cache
    if (this.contentCache) {
      this.contentCache.clear();
    }

    // Remove initialization marker
    this.section.removeAttribute('data-product-ingredients-allergens-initialized');

    console.log('ProductIngredientsAllergens: Component destroyed');
  }
}

// Auto-initialize when DOM is ready
document.addEventListener('DOMContentLoaded', function() {
  // Find all product ingredients & allergens sections
  const sections = document.querySelectorAll('[data-section-id]');

  sections.forEach(section => {
    const sectionId = section.dataset.sectionId;
    if (section.classList.contains('product-ingredients-allergens')) {
      new ProductIngredientsAllergens(sectionId);
    }
  });
});

// Export for use in other scripts
window.ProductIngredientsAllergens = ProductIngredientsAllergens;
